/**
 * A matriz pública (E153.F3, ADR-0104 §2.3 e §2.6): cada requisito da leitura com a interpretação
 * pública (resultado, código e Considerações) e, por expansão, o estado interno conservado —
 * situação, suporte, rota, elementos, o que falta, a convergência pessoa + ato e o motivo. A
 * leitura que sustenta vem primeiro; a ordem dos requisitos é a do servidor.
 */
import { Badge, Blueprint } from '@/components/ui';
import {
  leiturasNaOrdem,
  type LeituraDoResultadoData,
  type RequisitoDoResultadoData,
  type ResultadoAuditadoData,
} from '@/lib/resultadoAuditado';

import { VARIANTE_DO_PUBLICO, preencher, simOuNao, t, tf } from './textos';

function Par({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <>
      <dt className="text-fg-muted">{rotulo}</dt>
      <dd className="text-fg">{valor}</dd>
    </>
  );
}

function Interno({ r }: { r: RequisitoDoResultadoData }): JSX.Element {
  const m = t.matriz;
  const elementos =
    r.elementos.length === 0
      ? m.semElementos
      : r.elementos
          .map((e) => `${tf.tipoDoElemento[e.tipo]} (${tf.situacaoDoElemento[e.situacao]})`)
          .join('; ');
  return (
    <details className="mt-sc-1" data-testid="resultado-requisito-interno">
      <summary className="cursor-pointer text-caption text-accent-700">{m.interno}</summary>
      <dl className="mt-sc-1 grid grid-cols-[auto_1fr] gap-x-sc-3 gap-y-sc-1 text-caption">
        <Par rotulo={m.situacaoInterna} valor={tf.situacaoDoRequisito[r.situacao]} />
        <Par rotulo={m.suporte} valor={r.suporte === null ? m.semSuporte : t.suporte[r.suporte]} />
        <Par rotulo={m.rota} valor={r.rota ?? m.semSuporte} />
        <Par rotulo={m.elementos} valor={elementos} />
        <Par rotulo={m.convergencia} valor={simOuNao(r.convergencia_pessoa_e_ato)} />
        <Par rotulo={m.motivo} valor={r.motivo} />
      </dl>
      {r.faltam.length > 0 ? (
        <p className="mt-sc-1 text-caption text-fg-muted" data-testid="resultado-requisito-faltam">
          {preencher(m.faltam, { itens: r.faltam.join('; ') })}
        </p>
      ) : null}
    </details>
  );
}

function Requisito({ r }: { r: RequisitoDoResultadoData }): JSX.Element {
  const p = r.publica;
  return (
    <li
      className="border-l-2 border-border pl-sc-3"
      data-testid="resultado-requisito"
      data-requisito={r.requisito_id}
      data-publico={p.resultado}
    >
      <p className="text-fg">
        {r.titulo}{' '}
        <Badge variant={VARIANTE_DO_PUBLICO[p.resultado]}>{t.publico[p.resultado]}</Badge>{' '}
        <span className="text-caption text-fg-muted">
          {t.matriz.codigo}: {t.reasonCode[p.reason_code]}
        </span>
      </p>
      <p className="text-fg-muted">
        <span className="text-caption">{t.matriz.consideracoes}:</span> {p.consideracoes}
      </p>
      <Interno r={r} />
    </li>
  );
}

function Leitura({ l }: { l: LeituraDoResultadoData }): JSX.Element {
  return (
    <section
      className="space-y-sc-2"
      data-testid="resultado-leitura"
      data-leitura={`${l.perfil_id}/${l.leitura_id}`}
      data-escolhida={l.escolhida}
      aria-label={l.titulo}
    >
      <h3 className="text-body-md font-semibold text-fg">
        {l.titulo} {l.escolhida ? <Badge variant="info">{t.matriz.escolhida}</Badge> : null}{' '}
        {l.subsidiaria ? <Badge>{t.matriz.subsidiaria}</Badge> : null}{' '}
        <Badge>{t.situacaoDaLeitura[l.situacao]}</Badge>{' '}
        <span className="text-caption text-fg-muted">
          {preencher(t.matriz.prioridade, { n: l.prioridade })}
        </span>
      </h3>
      <p className="text-body-sm text-fg-muted">{l.motivo}</p>
      <ul className="space-y-sc-2 text-body-sm">
        {l.requisitos.map((r) => (
          <Requisito key={r.requisito_id} r={r} />
        ))}
      </ul>
    </section>
  );
}

export function MatrizPublicaDoResultado({
  resultado,
}: {
  resultado: ResultadoAuditadoData;
}): JSX.Element {
  const leituras = leiturasNaOrdem(resultado);
  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="resultado-matriz"
      aria-labelledby="resultado-matriz-titulo"
    >
      <div>
        <h2 id="resultado-matriz-titulo" className="text-body-lg text-fg">
          {t.matriz.titulo}
        </h2>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.matriz.subtitulo}</p>
      </div>
      {leituras.length === 0 ? (
        <p className="text-body-sm text-fg-muted">{t.matriz.semLeituras}</p>
      ) : (
        leituras.map((l) => <Leitura key={`${l.perfil_id}/${l.leitura_id}`} l={l} />)
      )}
    </Blueprint>
  );
}
